import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HardDriveDownload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const box = "rounded-2xl border border-slate-800 bg-slate-900/60 p-5";
const btn = "rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50";

export const AdminDriveBackup = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/admin/drive/status`, { headers }).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (label, fn) => {
    setBusy(true);
    try { const r = await fn(); toast.success(label); load(); return r; }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
    finally { setBusy(false); }
  };
  const connect = () => run("Redirection vers Google…", async () => { const { data } = await axios.get(`${API}/admin/drive/connect`, { headers }); window.location.href = data.authorization_url; });
  const copyNow = () => run("Copie envoyée sur Google Drive", () => axios.post(`${API}/admin/drive/run`, {}, { headers }));
  const disconnect = () => window.confirm("Déconnecter Google Drive ? Les copies déjà envoyées restent dans votre Drive.") && run("Google Drive déconnecté", () => axios.post(`${API}/admin/drive/disconnect`, {}, { headers }));

  if (!st) return null;
  return (
    <div data-testid="admin-drive" className={box}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><HardDriveDownload className="w-4 h-4 text-[#3A5CC8]" /> Second fournisseur : Google Drive</h3>
        <span className="flex gap-2">
          {st.connected ? (
            <>
              <button type="button" data-testid="drive-run" disabled={busy} onClick={copyNow} className={`${btn} bg-[#3A5CC8] text-white hover:bg-[#2F4CA8]`}>Envoyer la dernière sauvegarde</button>
              <button type="button" data-testid="drive-disconnect" disabled={busy} onClick={disconnect} className={`${btn} border border-slate-700 hover:bg-slate-800`}>Déconnecter</button>
            </>
          ) : (
            <button type="button" data-testid="drive-connect" disabled={busy || !st.configured} onClick={connect} className={`${btn} bg-[#3A5CC8] text-white hover:bg-[#2F4CA8]`}>Connecter Google Drive</button>
          )}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-400">Chaque semaine, la copie chiffrée est aussi déposée dans le dossier « {st.folder_name} » de votre Google Drive (accès limité aux fichiers créés par Devisto, rétention 90 jours). Les sauvegardes restent illisibles sans la clé de chiffrement Devisto.</p>
      {!st.configured && (
        <p data-testid="drive-not-configured" className="mt-3 rounded-lg border border-amber-900/60 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
          Non configuré : ajoutez les clés GOOGLE_CLIENT_ID et GOOGLE_CLIENT_SECRET (console Google Cloud → identifiants OAuth, API Drive activée, URI de redirection <code>{window.location.origin}/api/admin/drive/callback</code>), puis revenez ici pour connecter votre compte.
        </p>
      )}
      {st.connected && (
        <p data-testid="drive-status" className="mt-3 text-sm">
          <span className="text-emerald-400">Connecté</span>{st.account ? ` · ${st.account}` : ""}
          {st.last_upload_at ? ` · dernière copie ${new Date(st.last_upload_at).toLocaleString("fr-BE")} (${st.last_file})` : " · aucune copie envoyée pour l'instant"}
        </p>
      )}
    </div>
  );
};
